import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { borderCrossingsApi, normalizeBorderCrossing } from '../../api/borderCrossingsApi';
import { useLocationOptions } from '../Admin/shared/useLocationOptions';
import { useBorderRegistryReferenceOptions } from './useMarketTradReferences';
import { SelectWithOther } from '../reusable/formRecords/formFieldPrimitives';
import {
  BORDER_DIRECTION_OPTIONS,
  BORDER_POINT_OPTIONS,
  INSPECTION_STATUS_OPTIONS,
  labelForValue,
} from './marketTradOptions';
import NlimsDataTable from '../shared/NlimsDataTable/NlimsDataTable';
import { FilterBar, SearchRow } from './shared';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  formatOtherOptionValue,
  splitOtherOptionValue,
} from '../shared/forms/otherFieldHelpers';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, actionCellHtml as sharedActionCellHtml, WORKFLOW_RULES } from '../shared/RowActionMenu';

function normalizeForCompare(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Client-side filtering, mirroring the dropdown filter set — the backend
// doesn't reliably honor these query params, so filters are applied here
// against the already-loaded records instead (same approach Conflicts &
// Dispute Records uses).
function filterBorderCrossingRows(rows, filters) {
  const state = normalizeForCompare(filters.state);
  const species = normalizeForCompare(filters.species_uuid);
  const direction = normalizeForCompare(filters.direction);
  const dataSource = normalizeForCompare(filters.data_source);

  return rows.filter((row) => {
    if (state && normalizeForCompare(row.state) !== state) return false;
    if (species) {
      const rowSpeciesUuid = normalizeForCompare(row.species_uuid ?? row.speciesUuid);
      const rowSpeciesCode = normalizeForCompare(row.species_code ?? row.speciesCode);
      if (rowSpeciesUuid !== species && rowSpeciesCode !== species) return false;
    }
    if (direction && normalizeForCompare(row.direction ?? row.crossing_direction) !== direction) return false;
    if (dataSource && normalizeForCompare(row.data_source ?? row.dataSource) !== dataSource) return false;
    return true;
  });
}

function parseError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function formatCount(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return '—';
  return number.toLocaleString('en-NG');
}

function buildLookup(options = []) {
  const map = new Map();
  options.forEach((option) => {
    const raw = option?.raw ?? {};
    [option?.value, raw.uuid, raw.id, raw.code, raw.species_code, raw.name]
      .filter(Boolean)
      .forEach((key) => map.set(String(key), option.label ?? option.value ?? String(key)));
  });
  return map;
}

function normalizeBorderRow(row = {}, speciesLookup, borderPointLookup = new Map()) {
  const normalized = normalizeBorderCrossing(row);
  const speciesKey = [
    normalized.species_uuid, normalized.speciesUuid,
    normalized.species_code, normalized.speciesCode,
  ].find(Boolean);
  const borderPointKey = [
    normalized.border_point_id,
    normalized.borderPointId,
    normalized.crossing_point,
    normalized.crossingPoint,
  ].find(Boolean);
  const borderPointLabel =
    (borderPointKey && borderPointLookup.get(String(borderPointKey))) ||
    normalized.border_point_label ||
    normalized.borderPointLabel ||
    String(borderPointKey ?? '');
  return {
    ...normalized,
    species_label:
      (speciesKey && speciesLookup.get(String(speciesKey))) ||
      normalized.species_label ||
      normalized.speciesLabel ||
      String(speciesKey ?? ''),
    border_point_label: borderPointLabel,
    borderPointLabel,
    crossing_point: borderPointLabel || normalized.crossing_point || normalized.crossingPoint || String(borderPointKey ?? ''),
    crossingPoint: borderPointLabel || normalized.crossing_point || normalized.crossingPoint || String(borderPointKey ?? ''),
  };
}

function statusBadgeHtml(status) {
  if (!status) return '—';
  const key = String(status).trim().toLowerCase();
  const styleMap = {
    // Workflow pipeline — exact spec colours
    draft:     'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;',
    submitted: 'background:#eff6ff;color:#185FA5;border:1px solid #bfdbfe;',
    validated: 'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
    approved:  'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    rejected:  'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
    // Border crossing inspection statuses
    cleared:   'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    held:      'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
    active:    'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    inactive:  'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
  };
  const style = styleMap[key] || 'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;letter-spacing:.02em;white-space:nowrap;${style}">${status}</span>`;
}

// ── Shared edit-form primitives ───────────────────────────────────────────────
const INPUT_CLS =
  'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white ' +
  'focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 appearance-none';

function EField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function EInput({ value, onChange, placeholder = '', type = 'text', step }) {
  return (
    <input
      type={type}
      step={step}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      className={INPUT_CLS}
    />
  );
}

function ESelect({ value, onChange, options = [], placeholder = 'Select', disabled = false }) {
  return (
    <select
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      className={`${INPUT_CLS} disabled:bg-gray-50 disabled:text-gray-400`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : String(o.value ?? '');
        const l = typeof o === 'string' ? o : String(o.label ?? v);
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

// ── Role capability matrix (matches system RBAC spec) ────────────────────────
// R01: System Admin — full CRUD + validate + approve
// R02: National Data Manager — read/validate/approve only, no create/edit/delete
// R03: Federal Dept Officer — create/read/update/validate (border states), no approve
// R04: State Data Officer — create/read/update/validate (own state), no approve
// R05: LGA Field Officer — no access to border crossings
const ROLE_CAN = {
  R01: { create: true,  edit: true,  submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { create: false, edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { create: true,  edit: true,  submit: true,  validate: true,  approve: false, reject: true,  delete: false },
  R04: { create: true,  edit: true,  submit: true,  validate: true,  approve: false, reject: false, delete: false },
  R05: { create: false, edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
};

// Per-role status-transition map, replicating getMenuItems()'s exact logic:
// submit/validate/approve thresholds are the same for every role with that
// capability, but Reject differs — R02 (national reviewer) may only reject
// an already-Validated record, everyone else (R01/R03) can reject at
// Submitted or Validated. Passed as `transitions` to the shared ActionDropdown.
const BORDER_TRANSITIONS = {
  R01: { draft: ['submitted'], rejected: ['submitted'], submitted: ['validated', 'rejected'], validated: ['approved', 'rejected'] },
  R02: { validated: ['approved', 'rejected'] },
  R03: { draft: ['submitted'], rejected: ['submitted'], submitted: ['validated', 'rejected'], validated: ['rejected'] },
  R04: { draft: ['submitted'], rejected: ['submitted'], submitted: ['validated'] },
  R05: {},
};

// R01 may edit at any status; R03 while Draft/Rejected/Submitted; R04 only
// while Draft/Rejected. Matches the original getMenuItems() showEdit rule.
const BORDER_EDITABLE_STATUSES = {
  R01: ['draft', 'submitted', 'validated', 'approved', 'rejected'],
  R03: ['draft', 'rejected', 'submitted'],
  R04: ['draft', 'rejected'],
};

// ── Detail row ────────────────────────────────────────────────────────────────
function InfoRow({ label, value, wide = false }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-800 break-words">{value || '—'}</p>
    </div>
  );
}

function resolveBorderPointLabel(row = {}) {
  return row.border_point_label ?? row.borderPointLabel ?? row.crossing_point ?? row.crossingPoint ?? row.border_point_id ?? row.borderPointId ?? '';
}

function resolveMovementDate(row = {}) {
  return row.movement_date ?? row.movementDate ?? row.crossing_date ?? row.crossingDate ?? '';
}

function resolveOriginDestination(row = {}) {
  const fallback =
    [row.origin_country, row.destination_country].filter(Boolean).join(' / ') ||
    [row.origin_state, row.destination_state].filter(Boolean).join(' / ') ||
    '';

  return row.origin_destination ?? row.originDestination ?? fallback;
}

// ── Unified record modal (view → edit, with inline delete confirm) ─────────────
function BorderRecordModal({
  row,
  stateOptions,
  speciesOptions,
  borderPointOptions,
  vetCertificateOptions,
  dataSourceOptions,
  onSave,
  onDelete,
  onClose,
}) {
  const [mode, setMode] = useState('view');
  const [showDel, setShowDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState('');
  const borderPointLabel = resolveBorderPointLabel(row) || labelForValue(borderPointOptions, row.border_point_id ?? row.borderPointId);
  const movementDate = resolveMovementDate(row);
  const originDestination = resolveOriginDestination(row);

  const handleDelete = async () => {
    setDelBusy(true);
    setDelError('');
    try { await onDelete(row.uuid); }
    catch (err) { setDelError(parseError(err, 'Failed to delete.')); setDelBusy(false); }
  };

  const CloseBtn = () => (
    <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  if (showDel) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-6">
          <h3 className="text-base font-bold text-gray-900 mb-2">Delete Record</h3>
          <p className="text-sm text-gray-600 mb-1">
            Are you sure you want to delete <strong className="text-gray-900">{resolveBorderPointLabel(row) || 'this record'}</strong>? This action cannot be undone.
          </p>
          {delError && <p className="text-xs text-red-600 mt-2">{delError}</p>}
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setShowDel(false); setDelError(''); }} className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={handleDelete} disabled={delBusy} className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {delBusy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'edit') {
    return (
      <BorderEditForm
        row={row}
        stateOptions={stateOptions}
        speciesOptions={speciesOptions}
        borderPointOptions={borderPointOptions}
        vetCertificateOptions={vetCertificateOptions}
        dataSourceOptions={dataSourceOptions}
        onSave={onSave}
        onBack={() => setMode('view')}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-green-100 bg-green-50 px-6 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-green-600">Record Details</p>
            <h3 className="mt-0.5 text-sm font-semibold text-green-900">{borderPointLabel || 'Border Crossing'}</h3>
          </div>
          <CloseBtn />
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 px-6 py-5">
          <InfoRow label="State" value={row.state} />
          <InfoRow label="Border Point" value={borderPointLabel} />
          <InfoRow label="Origin / Destination" value={originDestination} />
          <InfoRow label="Movement Date" value={movementDate} />
          <InfoRow label="Crossing Direction" value={row.crossing_direction || row.direction} />
          <InfoRow label="Origin Country" value={row.origin_country} />
          <InfoRow label="Destination Country" value={row.destination_country} />
          <InfoRow label="Livestock Species" value={row.species_label} />
          <InfoRow label="Head Count" value={formatCount(row.head_count)} />
          <InfoRow label="Vet Certificate Ref" value={row.vet_certificate_ref || row.vetCertificateRef} />
          <InfoRow label="Movement Permit Number" value={row.movement_permit_number || row.movementPermitNumber} />
          <InfoRow label="Inspection Status" value={row.inspection_status || row.status} />
          <InfoRow label="Data Source" value={row.data_source} />
          <InfoRow label="Notes" value={row.notes} wide />
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">Close</button>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDel(true)} className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
            <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit form (used inside BorderRecordModal) ─────────────────────────────────
function BorderEditForm({
  row,
  stateOptions,
  speciesOptions,
  borderPointOptions,
  vetCertificateOptions,
  dataSourceOptions,
  onSave,
  onBack,
  onClose,
}) {
  const { user } = useAuth();
  // R04 (State Data Officer) is the field/originator role for Border Crossing records —
  // R05 has no access to this tab, R01/R03 keep the existing single "Save Changes" behaviour.
  const isFieldUser = user?.roleId === 'R04';
  const currentStatus = (row.data_status ?? row.workflow_status ?? row.validation_status ?? 'draft').toLowerCase();
  const borderPointChoices = borderPointOptions?.length ? borderPointOptions : BORDER_POINT_OPTIONS;
  const { value: dataSourceValue, otherValue: dataSourceOther } = splitOtherOptionValue(
    row.data_source ?? row.dataSource ?? '',
    dataSourceOptions
  );
  const [f, setF] = useState({
    state:               row.state               ?? '',
    border_point_id:     row.border_point_id     ?? row.crossing_point ?? '',
    crossing_direction:  row.crossing_direction  ?? row.direction ?? '',
    movement_date:       row.movement_date       ?? row.crossing_date ?? '',
    origin_country:      row.origin_country      ?? '',
    destination_country: row.destination_country ?? '',
    species_uuid:        row.species_uuid        ?? '',
    head_count:          String(row.head_count   ?? ''),
    vehicle_id:          row.vehicle_id          ?? '',
    agent_name:          row.agent_name          ?? '',
    vet_certificate_ref: row.vet_certificate_ref ?? '',
    movement_permit_number: row.movement_permit_number ?? '',
    inspection_status:   row.inspection_status   ?? row.status ?? '',
    data_source:         dataSourceValue,
    data_source_other:   dataSourceOther,
    notes:               row.notes               ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const canSave =
    !!f.border_point_id &&
    !!f.crossing_direction &&
    !!f.movement_date &&
    !!(f.origin_country.trim() || f.destination_country.trim()) &&
    !!f.species_uuid &&
    Number(f.head_count) > 0 &&
    !!f.inspection_status &&
    !!f.data_source &&
    (String(f.data_source).toLowerCase() !== 'other' || !!f.data_source_other.trim());

  const buildPayload = (extra = {}) => {
    const borderPointLabel = labelForValue(borderPointChoices, f.border_point_id);
    const originDestination =
      [f.origin_country.trim(), f.destination_country.trim()].filter(Boolean).join(' / ') ||
      f.state.trim();
    return {
      state:               f.state || null,
      border_point_id:     f.border_point_id,
      borderPointId:       f.border_point_id,
      border_point_label:   borderPointLabel || null,
      borderPointLabel:     borderPointLabel || null,
      crossing_point:      f.border_point_id,
      crossingPoint:       f.border_point_id,
      crossing_direction:  f.crossing_direction,
      crossingDirection:   f.crossing_direction,
      direction:           f.crossing_direction,
      movement_date:       f.movement_date,
      movementDate:        f.movement_date,
      crossing_date:       f.movement_date,
      crossingDate:        f.movement_date,
      origin_country:      f.origin_country.trim() || null,
      destination_country: f.destination_country.trim() || null,
      origin_destination:  originDestination || null,
      originDestination:   originDestination || null,
      species_uuid:        f.species_uuid,
      head_count:          Number(f.head_count),
      vehicle_id:          f.vehicle_id.trim() || null,
      agent_name:          f.agent_name.trim() || null,
      vet_certificate_ref: f.vet_certificate_ref || null,
      vetCertificateRef:   f.vet_certificate_ref || null,
      movement_permit_number: f.movement_permit_number.trim() || null,
      movementPermitNumber: f.movement_permit_number.trim() || null,
      inspection_status:   f.inspection_status,
      status:              f.inspection_status,
      // Field/originator role (R04): saving always defaults the record back to draft —
      // advancing the workflow is a separate, explicit "Submit for Review" action.
      // Other roles (R01/R03): preserve whatever status the record currently has.
      data_status:         isFieldUser ? 'draft' : (row.data_status ?? row.workflow_status ?? row.validation_status ?? 'draft'),
      data_source:         formatOtherOptionValue(f.data_source, f.data_source_other, dataSourceOptions) || null,
      notes:               f.notes.trim() || null,
      raw:                 row.raw,
      ...extra,
    };
  };

  const handleSave = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    setError('');
    try {
      await onSave(row.uuid, buildPayload());
      onClose();
    } catch (err) {
      setError(parseError(err, 'Failed to save changes.'));
      setBusy(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    setError('');
    try {
      await onSave(row.uuid, buildPayload({ submitAfterSave: true }));
      onClose();
    } catch (err) {
      setError(parseError(err, 'Failed to save changes.'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-green-100 bg-green-50 px-6 py-4 flex-shrink-0">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-green-600">Edit Border Crossing</p>
            <h3 className="mt-0.5 text-sm font-semibold text-green-900">{resolveBorderPointLabel(row) || 'Record'}</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
          {error && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <EField label="State">
              <ESelect value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />
            </EField>
            <EField label="Border Point" required>
              <ESelect value={f.border_point_id} onChange={set('border_point_id')} options={borderPointChoices} placeholder="Select border point" />
            </EField>
            <EField label="Crossing Direction" required>
              <ESelect value={f.crossing_direction} onChange={set('crossing_direction')} options={BORDER_DIRECTION_OPTIONS} placeholder="Select direction" />
            </EField>
            <EField label="Movement Date" required>
              <EInput type="date" value={f.movement_date} onChange={set('movement_date')} />
            </EField>
            <EField label="Origin Country">
              <EInput value={f.origin_country} onChange={set('origin_country')} placeholder="e.g. Niger, Chad" />
            </EField>
            <EField label="Destination Country">
              <EInput value={f.destination_country} onChange={set('destination_country')} placeholder="e.g. Nigeria" />
            </EField>
            <EField label="Livestock Species" required>
              <ESelect value={f.species_uuid} onChange={set('species_uuid')} options={speciesOptions} placeholder="Select species" />
            </EField>
            <EField label="Head Count" required>
              <EInput type="number" step="1" value={f.head_count} onChange={set('head_count')} placeholder="0" />
            </EField>
            <EField label="Vehicle ID">
              <EInput value={f.vehicle_id} onChange={set('vehicle_id')} placeholder="e.g. BR-12-AB" />
            </EField>
            <EField label="Agent Name">
              <EInput value={f.agent_name} onChange={set('agent_name')} placeholder="Optional trader / agent name" />
            </EField>
            <EField label="Vet Certificate Ref">
              <ESelect value={f.vet_certificate_ref} onChange={set('vet_certificate_ref')} options={vetCertificateOptions} placeholder="Select from registry" />
            </EField>
            <EField label="Movement Permit Number">
              <EInput value={f.movement_permit_number} onChange={set('movement_permit_number')} placeholder="Optional permit number" />
            </EField>
            <EField label="Inspection Status" required>
              <ESelect value={f.inspection_status} onChange={set('inspection_status')} options={INSPECTION_STATUS_OPTIONS} placeholder="Select status" />
            </EField>
            <SelectWithOther
              label="Data Source"
              required
              value={f.data_source}
              otherValue={f.data_source_other}
              onChange={(value) => setF((p) => ({
                ...p,
                data_source: value,
                data_source_other: String(value).toLowerCase() === 'other' ? p.data_source_other : '',
              }))}
              onOtherChange={(value) => setF((p) => ({ ...p, data_source_other: value }))}
              options={dataSourceOptions}
              placeholder="Select source"
              otherPlaceholder="Describe the source"
            />
          </div>
          <EField label="Notes">
            <textarea value={f.notes} onChange={set('notes')} rows={2} placeholder="Optional notes" className={`${INPUT_CLS} resize-none`} />
          </EField>
        </div>
        <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
          <button onClick={onBack} className="flex-1 h-11 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          {isFieldUser ? (
            <>
              <button onClick={handleSave} disabled={!canSave || busy} className="flex-1 h-11 rounded-full border border-green-300 bg-white text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? 'Saving…' : (currentStatus === 'rejected' ? 'Save Changes' : 'Save as Draft')}
              </button>
              <button onClick={handleSaveAndSubmit} disabled={!canSave || busy} className="flex-1 h-11 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? 'Submitting…' : 'Submit for Review'}
              </button>
            </>
          ) : (
            <button onClick={handleSave} disabled={!canSave || busy} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Stat cards ────────────────────────────────────────────────────────────────
function StatCard({ title, value, valueColor, legend, subtitle }) {
  return (
    <div className="flex-1 min-w-0 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm text-gray-500 leading-snug">{title}</p>
      <p className={`mt-2 text-[2rem] font-bold leading-none ${valueColor}`}>{value}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      {legend && <p className="mt-1.5 text-[11px] text-gray-400 leading-snug">{legend}</p>}
    </div>
  );
}

// ── Border type helpers ───────────────────────────────────────────────────────
function isNigeriaCountry(val) {
  if (!val) return true;
  const s = String(val).toLowerCase().trim();
  return s === '' || s === 'nigeria' || s === 'ng' || s === 'nig';
}

function getRowBorderType(row) {
  if (row.origin_state || row.destination_state) return 'national';
  const originDestination = String(resolveOriginDestination(row)).toLowerCase();
  if (originDestination.includes('nigeria') || /\bng\b/.test(originDestination)) return 'national';
  if (originDestination) return 'international';
  return isNigeriaCountry(row.origin_country) && isNigeriaCountry(row.destination_country)
    ? 'national' : 'international';
}

// ── Table columns ─────────────────────────────────────────────────────────────
function buildTableColumns() {
  return [
    { title: 'State', data: 'state', sortable: true },
    {
      title: 'Border Point',
      data: 'border_point_label',
      sortable: true,
      render: (data, type, row) => (type === 'display' ? (data || row.crossing_point || '—') : (data ?? row.crossing_point ?? '')),
    },
    {
      title: 'Movement Date',
      data: 'movement_date',
      sortable: true,
      render: (data, type, row) => (type === 'display' ? (data || row.crossing_date || '—') : (data ?? row.crossing_date ?? '')),
    },
    {
      title: 'Crossing Direction',
      data: 'crossing_direction',
      sortable: true,
      render: (data, type) => {
        if (type !== 'display') return data ?? '';
        const label = labelForValue(BORDER_DIRECTION_OPTIONS, data);
        return label || data || '—';
      },
    },
    {
      title: 'Origin Country', data: 'origin_country', sortable: true,
      render: (d, t, row) => (t === 'display' ? (d || row.origin_destination?.split('/')?.[0]?.trim() || '—') : (d ?? '')),
    },
    {
      title: 'Destination Country', data: 'destination_country', sortable: true,
      render: (d, t, row) => (t === 'display' ? (d || row.origin_destination?.split('/')?.[1]?.trim() || '—') : (d ?? '')),
    },
    { title: 'Species', data: 'species_label', sortable: true },
    {
      title: 'Head Count',
      data: 'head_count',
      sortable: true,
      render: (data, type) => (type === 'display' ? formatCount(data) : (data ?? 0)),
    },
    { title: 'Vet Certificate Ref', data: 'vet_certificate_ref', sortable: true },
    { title: 'Movement Permit No.', data: 'movement_permit_number', sortable: true },
    { title: 'Inspection Status', data: 'inspection_status', sortable: true, render: (data, type) => (type === 'display' ? statusBadgeHtml(data) : (data ?? '')) },
    { title: 'Data Source', data: 'data_source', sortable: true },
    { title: 'Workflow Status', data: 'data_status', sortable: true, render: (data, type) => (type === 'display' ? statusBadgeHtml(data || 'draft') : (data ?? 'draft')) },
    {
      title: 'Action',
      data: null,
      sortable: false,
      render: (_d, type) => (type === 'display' ? sharedActionCellHtml() : ''),
    },
  ];
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BorderCrossingRegistry({ onCreateNew, refreshKey = 0, newRecord }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const isR05 = roleId === 'R05';
  const { pushWorkflowNotification } = useNotifications();
  const [borderType, setBorderType] = useState('all');
  const [draftFilters, setDraftFilters] = useState({
    state: '', species_uuid: '', direction: '', data_source: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    state: '', species_uuid: '', direction: '', data_source: '',
  });
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState('');

  const { stateOptions, loadingStates } = useLocationOptions();
  const {
    speciesOptions,
    borderPointOptions,
    vetCertificateOptions,
    dataSourceOptions,
    loading: referenceLoading,
    error: referenceError,
  } = useBorderRegistryReferenceOptions();

  const speciesLookup = useMemo(() => buildLookup(speciesOptions), [speciesOptions]);
  const borderPointLookup = useMemo(() => buildLookup(borderPointOptions), [borderPointOptions]);
  // Keep a ref so the fetch effect always uses the latest lookup without re-triggering
  const speciesLookupRef = useRef(speciesLookup);
  const borderPointLookupRef = useRef(borderPointLookup);
  useEffect(() => { speciesLookupRef.current = speciesLookup; }, [speciesLookup]);
  useEffect(() => { borderPointLookupRef.current = borderPointLookup; }, [borderPointLookup]);
  useEffect(() => {
    setRecords((prev) => prev.map((row) => normalizeBorderRow(row, speciesLookupRef.current, borderPointLookupRef.current)));
  }, [speciesLookup, borderPointLookup]);

  const tableColumns = useMemo(() => buildTableColumns(), []);

  // Insert freshly created record into local state (avoids re-fetch which loses status)
  useEffect(() => {
    if (!newRecord?.uuid) return;
    const normalized = normalizeBorderRow(newRecord, speciesLookupRef.current, borderPointLookupRef.current);
    setRecords((prev) => {
      if (prev.some((r) => r.uuid === normalized.uuid)) return prev;
      return [normalized, ...prev];
    });
  }, [newRecord]);

  const filterDefs = useMemo(() => ([
    {
      key: 'state',
      label: 'State',
      options: stateOptions,
      defaultLabel: loadingStates ? 'Loading States...' : 'All States',
    },
    {
      key: 'species_uuid',
      label: 'Livestock Species',
      options: speciesOptions,
      defaultLabel: referenceLoading ? 'Loading species...' : 'All Livestock Species',
    },
    {
      key: 'direction',
      label: 'Crossing Direction',
      options: BORDER_DIRECTION_OPTIONS,
      defaultLabel: 'All Directions',
    },
    {
      key: 'data_source',
      label: 'Data Source',
      options: dataSourceOptions,
      defaultLabel: referenceLoading ? 'Loading sources...' : 'All Sources',
    },
  ]), [dataSourceOptions, loadingStates, referenceLoading, speciesOptions, stateOptions]);

  // Fetch once (search + refreshKey only) — dropdown filters are applied
  // client-side below (dropdownFilteredRecords), not sent to the backend,
  // since it doesn't reliably honor these query params.
  // speciesLookup is accessed via ref so it doesn't trigger extra re-fetches
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    borderCrossingsApi.listAll({
      per_page: 1000,
    })
      .then((payload) => {
        if (!active) return;
        setRecords((payload.items || []).map((row) => normalizeBorderRow(row, speciesLookupRef.current, borderPointLookupRef.current)));
      })
      .catch((err) => {
        if (!active) return;
        setError(parseError(err, 'Failed to load border crossing records.'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Applies immediately on every change — live filtering, matching the rest
  // of the app, instead of requiring a separate "Apply" click.
  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      setAppliedFilters(next);
      return next;
    });
  }, []);

  const handleApply = useCallback(() => setAppliedFilters(draftFilters), [draftFilters]);
  const handleCreate = useCallback(() => {
    onCreateNew?.({ subModule: 'border', borderScope: borderType });
  }, [borderType, onCreateNew]);

  const dropdownFilteredRecords = useMemo(
    () => filterBorderCrossingRows(records, appliedFilters),
    [records, appliedFilters]
  );

  // Stats derived from type-scoped records ('all' shows everything)
  const typeFilteredRecords = useMemo(
    () => borderType === 'all' ? dropdownFilteredRecords : dropdownFilteredRecords.filter((r) => getRowBorderType(r) === borderType),
    [dropdownFilteredRecords, borderType]
  );

  const stats = useMemo(() => {
    const total = typeFilteredRecords.length;
    const cleared = typeFilteredRecords.filter((r) => String(r.inspection_status || r.status || '').toLowerCase() === 'cleared').length;
    const compliant = typeFilteredRecords.filter((r) =>
      ['validated', 'approved', 'cleared'].includes(String(r.inspection_status || r.status || '').toLowerCase())
    ).length;
    const uniquePoints = new Set(typeFilteredRecords.map((r) => resolveBorderPointLabel(r)).filter(Boolean)).size;
    return {
      total,
      clearanceRate: total > 0 ? Math.round((cleared / total) * 100) : 0,
      complianceRate: total > 0 ? Math.round((compliant / total) * 100) : 0,
      uniquePoints,
    };
  }, [typeFilteredRecords]);

  // Search is client-side on top of the server-filtered + type-filtered records
  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    return typeFilteredRecords.filter((row) => {
      if (!q) return true;
      return [
        row.state,
        resolveBorderPointLabel(row),
        resolveMovementDate(row),
        row.crossing_direction,
        resolveOriginDestination(row),
        row.species_label,
        row.vet_certificate_ref,
        row.movement_permit_number,
        row.data_source,
        row.inspection_status,
        row.notes,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [typeFilteredRecords, search]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action === 'view') setActionMenu({ row, rect });
  }, []);

  const reloadRecords = useCallback(() => {
    setLoading(true);
    setError('');
    borderCrossingsApi.listAll({
      per_page: 1000,
    })
      .then((payload) => setRecords((payload.items || []).map((row) => normalizeBorderRow(row, speciesLookupRef.current, borderPointLookupRef.current))))
      .catch((err) => setError(parseError(err, 'Failed to load border crossing records.')))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickSubmit = useCallback(async (row) => {
    setQuickBusy('Submitting…');
    try {
      await borderCrossingsApi.submit(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN, newStatus: 'submitted', actorRoleId: roleId, recordLabel: resolveBorderPointLabel(row) });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Submit failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleQuickValidate = useCallback(async (row) => {
    setQuickBusy('Validating…');
    try {
      await borderCrossingsApi.validate(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN, newStatus: 'validated', actorRoleId: roleId, recordLabel: resolveBorderPointLabel(row) });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Validation failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleQuickApprove = useCallback(async (row) => {
    setQuickBusy('Approving…');
    try {
      await borderCrossingsApi.approve(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN, newStatus: 'approved', actorRoleId: roleId, recordLabel: resolveBorderPointLabel(row) });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Approval failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy('Rejecting…');
    try {
      await borderCrossingsApi.reject(rejectTarget.uuid, notes);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN, newStatus: 'rejected', actorRoleId: roleId, recordLabel: resolveBorderPointLabel(rejectTarget), notes });
      setRejectTarget(null);
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Reject failed.')); throw e; }
    finally { setQuickBusy(''); }
  }, [rejectTarget, reloadRecords, roleId, pushWorkflowNotification]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy('Deleting…');
    try { await borderCrossingsApi.remove(deleteTarget.uuid); setDeleteTarget(null); reloadRecords(); }
    catch (e) { setError(parseError(e, 'Delete failed.')); }
    finally { setQuickBusy(''); }
  }, [deleteTarget, reloadRecords]);
  // Note: delete keeps the page-level error banner (unlike Reject) since
  // DeleteConfirmModal has no in-modal error slot — matches the original.

  const handleSave = useCallback(async (uuid, payload) => {
    const { submitAfterSave, ...body } = payload;
    // Confirm the update succeeded (throws on error)
    await borderCrossingsApi.update(uuid, body);
    if (submitAfterSave) {
      await borderCrossingsApi.submit(uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN, newStatus: 'submitted', actorRoleId: roleId, recordLabel: resolveBorderPointLabel(body) });
    }
    // Use payload values (what the user submitted) for the local optimistic patch;
    // don't trust the server response shape, which may omit status and other fields.
    setRecords((prev) =>
      prev.map((r) => {
        if (r.uuid !== uuid) return r;
        const status = body.inspection_status || body.status || r.inspection_status || r.status || '';
        const dataStatus = submitAfterSave
          ? 'submitted'
          : (body.data_status || body.dataStatus || r.data_status || r.dataStatus || r.workflow_status || 'draft');
        return normalizeBorderRow(
          { ...r, ...body, inspection_status: status, status, data_status: dataStatus, dataStatus },
          speciesLookup,
          borderPointLookupRef.current
        );
      })
    );
  }, [speciesLookup, roleId, pushWorkflowNotification]);

  const handleDelete = useCallback(async (uuid) => {
    await borderCrossingsApi.remove(uuid);
    setRecords((prev) => prev.filter((r) => r.uuid !== uuid));
    setViewRow(null);
  }, []);

  const complianceColor = stats.complianceRate >= 90 ? 'text-green-600' : stats.complianceRate >= 75 ? 'text-amber-500' : 'text-red-500';
  const clearanceColor  = stats.clearanceRate >= 95  ? 'text-green-600' : stats.clearanceRate >= 85  ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="space-y-4">
      {/* Stat cards — hidden for R05 */}
      {!isR05 && (
        <div className="flex gap-4">
          <StatCard
            title="Border Crossings This Period"
            value={stats.total.toLocaleString('en-NG')}
            valueColor="text-green-600"
            subtitle="Trend (Green) vs. last month (Grey)"
          />
          <StatCard
            title="Vet Certificate Compliance"
            value={`${stats.complianceRate}%`}
            valueColor={complianceColor}
            legend="Green ≥90% / Amber 75-89% / Red <75%"
          />
          <StatCard
            title="Cleared vs. Held/Rejected"
            value={`${stats.clearanceRate}%`}
            valueColor={clearanceColor}
            legend="Green ≥95% / Amber 85-94% / Red <85%"
          />
          <StatCard
            title="Active Border Points"
            value={String(stats.uniquePoints)}
            valueColor="text-gray-900"
            legend="Unique registered crossing points"
          />
        </div>
      )}

      {!isR05 && (
        <FilterBar
          filters={filterDefs}
          filterValues={draftFilters}
          onFilterChange={handleFilterChange}
          onApply={handleApply}
        />
      )}

      {/* National / International / All toggle */}
      <div className="flex gap-2">
        {[
          { key: 'all',           label: 'All Records' },
          { key: 'national',      label: 'National Border' },
          { key: 'international', label: 'International Border' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setBorderType(key)}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
              borderType === key
                ? 'bg-green-50 text-green-700 border-green-300'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <SearchRow
        searchValue={search}
        onSearch={setSearch}
        onCreate={handleCreate}
        placeholder="Search by border point, destination, species, or certificate..."
        canCreate={ROLE_CAN[roleId]?.create === true}
      />

      {referenceError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{referenceError}</div>
      )}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading border crossing records...
        </div>
      )}

      <NlimsDataTable
        tableId="border-crossings-table"
        columns={tableColumns}
        data={filteredRecords}
        serverSide={false}
        hideSearch
        pageLength={25}
        onRowAction={handleRowAction}
        defaultSort={[2, 'desc']}
      />

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.borderCrossings}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/markets-trade/view/${actionMenu.row.uuid}?subModule=border`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/markets-trade/edit/${actionMenu.row.uuid}?subModule=border`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => { const r = actionMenu.row; setActionMenu(null); handleQuickSubmit(r); }}
          onValidate={() => { const r = actionMenu.row; setActionMenu(null); handleQuickValidate(r); }}
          onApprove={() => { const r = actionMenu.row; setActionMenu(null); handleQuickApprove(r); }}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {viewRow && (
        <BorderRecordModal
          row={viewRow}
          stateOptions={stateOptions}
          speciesOptions={speciesOptions}
          borderPointOptions={borderPointOptions}
          vetCertificateOptions={vetCertificateOptions}
          dataSourceOptions={dataSourceOptions}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setViewRow(null)}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={resolveBorderPointLabel(deleteTarget)}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={!!quickBusy}
        />
      )}
    </div>
  );
}
